/**
 * React glue for describe/: the live outcomeTemplates list (merged over the bundled snapshot) and a memoized
 * describer bound to the current env's outcomeMeta.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hlQueryKey, useHlEnv, useInfoClient } from '../hl/react';
import type { OutcomeMetaResponse, OutcomeSpec, QuestionSpec } from '../hl/types';
import { describeOutcome, describeQuestion, type DescribeOptions, type OutcomeDescription, type QuestionDescription } from './describe';
import { buildRegistry, type TemplateRegistry } from './templates';

/** outcomeTemplates for the env, merged over the snapshot. Never throws: on error the snapshot is used. */
export function useOutcomeTemplates(): TemplateRegistry {
  const env = useHlEnv();
  const info = useInfoClient();
  const q = useQuery({
    queryKey: hlQueryKey(env, 'outcomeTemplates'),
    queryFn: async ({ signal }) => {
      const r = await info.request<unknown>({ type: 'outcomeTemplates' }, { signal });
      return Array.isArray(r) ? (r as unknown[]) : [];
    },
    staleTime: 60 * 60_000,
    gcTime: 2 * 60 * 60_000,
  });
  return useMemo(() => buildRegistry(env, q.data), [env, q.data]);
}

export interface Describer {
  outcome: (o: OutcomeSpec) => OutcomeDescription;
  question: (q: QuestionSpec) => QuestionDescription;
  /** Description by outcome id, undefined when the id is not listed. */
  byId: (id: number) => OutcomeDescription | undefined;
  opts: DescribeOptions;
}

/**
 * Describer for the current env: pass the page's outcomeMeta (from useOutcomeMeta). Stable while meta and templates do
 * not change.
 */
export function useDescriber(meta: OutcomeMetaResponse | undefined): Describer {
  const templates = useOutcomeTemplates();
  return useMemo(() => {
    const opts: DescribeOptions = { templates };
    const ids = new Map((meta?.outcomes ?? []).map((o) => [o.outcome, o]));
    return {
      outcome: (o) => describeOutcome(o, meta, opts),
      question: (q) => describeQuestion(q, opts),
      byId: (id) => {
        const o = ids.get(id);
        return o ? describeOutcome(o, meta, opts) : undefined;
      },
      opts,
    };
  }, [meta, templates]);
}
